import React, { useState, useEffect } from 'react';
import { Lock, Check, Plus, AlertTriangle } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';
import { API_PATH, request } from '../../../config/api';

function arraysEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
}

function containsProfanity(text, list) {
    if (!text || typeof text !== 'string') return false;
    const lower = text.trim().toLowerCase();
    const normalized = lower.replace(/[^\w\s]/g, '');
    const tokens = normalized.split(/\s+/).filter(Boolean);
    for (const word of list) {
        if (tokens.includes(word)) return true;
        if (normalized.includes(word)) return true;
    }
    return false;
}

const WordCloudForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true, eventCode = null }) => {
    const multipleAnswers = content.settings?.multipleAnswers === true;
    const maxWords = multipleAnswers
        ? Math.min(20, Math.max(2, Number(content.settings?.responses) || 5))
        : 1;
    const characterLimitOn = content.settings?.characterLimit === true;
    const charLimit = characterLimitOn
        ? Math.min(200, Math.max(1, Number(content.settings?.charLimitValue) || 30))
        : 50;
    const profanityFilterOn = content.settings?.profanityFilter !== false;

    const initial = userAnswer?.words && Array.isArray(userAnswer.words)
        ? userAnswer.words.length ? [...userAnswer.words] : ['']
        : [''];
    const [words, setWords] = useState(initial);
    const [profanityError, setProfanityError] = useState('');
    const [profanityList, setProfanityList] = useState([]);

    useEffect(() => {
        if (userAnswer?.words && Array.isArray(userAnswer.words) && userAnswer.words.length > 0) {
            setWords([...userAnswer.words]);
        } else if (userAnswer == null) setWords(['']);
    }, [userAnswer]);

    useEffect(() => {
        if (!profanityFilterOn) return;
        let cancelled = false;
        (async () => {
            try {
                // eventCode lets the server combine the global list with the event owner's
                // own personal list, matching the authoritative check submitResponse does —
                // without it (e.g. this form rendering before an event context exists), the
                // global list alone is still returned.
                const url = eventCode
                    ? `${API_PATH}/join/profanity-words?eventCode=${encodeURIComponent(eventCode)}`
                    : `${API_PATH}/join/profanity-words`;
                const res = await request(url);
                const data = await res.json().catch(() => ({}));
                if (!cancelled && Array.isArray(data?.words)) setProfanityList(data.words);
            } catch {
                if (!cancelled) setProfanityList([]);
            }
        })();
        return () => { cancelled = true; };
    }, [profanityFilterOn, eventCode]);

    const updateWord = (i, val) => {
        setWords(w => w.map((x, j) => (j === i ? val : x)));
        if (profanityError) setProfanityError('');
    };
    const addWord = () => { if (words.length < maxWords) setWords(w => [...w, '']); };

    const handleSubmit = () => {
        if (isLocked) return;
        setProfanityError('');
        const filtered = words.filter(w => w.trim());
        const toSubmit = maxWords === 1 ? filtered.slice(0, 1) : filtered;
        if (profanityFilterOn && profanityList.length > 0 && toSubmit.some(w => containsProfanity(w, profanityList))) {
            setProfanityError('One or more words are not allowed. Please remove inappropriate words.');
            return;
        }
        onSubmit({ words: toSubmit });
    };

    const currentWords = words.filter(w => w.trim());
    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const submittedWords = Array.isArray(userAnswer?.words) ? userAnswer.words : [];
    const isUnchanged = hasSubmitted && arraysEqual(currentWords, submittedWords);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || !currentWords.length || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Share a word'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}
            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}
            {words.map((w, i) => (
                <div key={i} className={styles.wordInputWrap}>
                    <input
                        className={styles.textInput}
                        placeholder={characterLimitOn ? `Word ${i + 1} (max ${charLimit} chars)` : `Word ${i + 1}`}
                        value={w}
                        onChange={e => updateWord(i, e.target.value)}
                        disabled={optionsDisabled}
                        maxLength={charLimit}
                    />
                    {characterLimitOn && (
                        <span className={styles.charCount} aria-live="polite">
                            {w.length}/{charLimit}
                        </span>
                    )}
                </div>
            ))}
            {profanityError && (
                <p className={styles.profanityError} role="alert">
                    <AlertTriangle size={16} aria-hidden /> {profanityError}
                </p>
            )}
            {words.length < maxWords && (
                <button
                    type="button"
                    className={styles.submitBtnSecondary}
                    onClick={addWord}
                    disabled={optionsDisabled}
                >
                    <Plus size={16} aria-hidden /> Add another word
                </button>
            )}
            <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default WordCloudForm;
